/**
 * What happened when a promo code was redeemed, as the client sees it.
 *
 * Unlike `referral-outcome.ts`, nothing is classified from an error message here: the service
 * answers OK with an outcome for every business case, so this is a rename of its enum plus the two
 * outcomes only the client can be in (`retry`, `failed`). That is the whole reason the RPC was
 * written that way — a funnel read off thrown statuses is a funnel that moves whenever an
 * exception message does.
 */
export type PromoOutcome =
  /** Credits landed on the account. */
  | 'redeemed'
  /** This account already redeemed this code. Terminal, and effectively success to a user. */
  | 'already'
  /** No such code — mistyped, hand-edited, or a campaign that was removed. Terminal. */
  | 'unknown'
  /** The campaign is over. Terminal. */
  | 'expired'
  /** The code's redemption cap was reached by other accounts. Terminal; told to the user as "ended". */
  | 'exhausted'
  /** Not redeemable yet — the caller's metasite does not exist. Keep the code and try after onboarding. */
  | 'retry'
  /** An unrecognised failure. Kept for retry, and worth an error report. */
  | 'failed';

/** The outcomes after which the stored code is no longer worth keeping. */
export const TERMINAL_PROMO_OUTCOMES: ReadonlySet<PromoOutcome> = new Set<PromoOutcome>(['redeemed', 'already', 'unknown', 'expired', 'exhausted']);

/** What `/api/promo-redeem` answers. `credits` and `freeHours` are zero on every outcome but `redeemed`. */
export interface PromoRedeemBody {
  outcome: PromoOutcome;
  credits: number;
  freeHours: number;
}

/**
 * The Balance service's `RedeemPromoCodeResponse` → the route's body. `granted` is the code's
 * `RewardRule`; a code may grant credits, free-usage hours, or both. The service deliberately reports
 * no amount for a code this account already holds, so a non-`redeemed` outcome always carries zeros.
 */
export function promoRedeemBodyOf(response: {
  outcome?: string;
  granted?: { credits?: number; freeHours?: number };
}): PromoRedeemBody {
  const outcome = promoOutcomeOf(response.outcome);
  if (outcome !== 'redeemed') return { outcome, credits: 0, freeHours: 0 };
  return {
    outcome,
    credits: nonNegativeInt(response.granted?.credits),
    freeHours: nonNegativeInt(response.granted?.freeHours),
  };
}

function nonNegativeInt(value: number | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.trunc(value) : 0;
}

/**
 * The service's enum, as this client names it. An unrecognised value is `failed` rather than
 * silently `redeemed`: a new outcome added on the server must never be read as "credits arrived".
 */
export function promoOutcomeOf(serviceOutcome: string | undefined): PromoOutcome {
  switch (serviceOutcome) {
    case 'PROMO_OUTCOME_REDEEMED':
      return 'redeemed';
    case 'PROMO_OUTCOME_ALREADY_REDEEMED':
      return 'already';
    case 'PROMO_OUTCOME_UNKNOWN_CODE':
      return 'unknown';
    case 'PROMO_OUTCOME_EXPIRED':
      return 'expired';
    case 'PROMO_OUTCOME_EXHAUSTED':
      return 'exhausted';
    default:
      return 'failed';
  }
}

/**
 * Whether a thrown redemption was REFUSED before it ran — the platform's missing-permission answer, or the
 * service's own refusal of a call without a metasite. Both are raised before any grant, so the caller may
 * retry under another identity without paying twice. Read from the message because the platform's refusal
 * has been seen both with and without a `PERMISSION_DENIED` prefix (see `referral-outcome.ts`).
 */
export function isRedeemRefused(err: unknown): boolean {
  const text = err instanceof Error ? err.message : String(err ?? '');
  return text.includes('PERMISSION_DENIED') || text.includes('InsufficientPermissions') || text.includes('FORBIDDEN');
}
